import type { ProductItem, ProductData } from './types';

function normalizeProductIdForKey(raw: unknown): number | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  if (Number.isNaN(n) || n <= 0) return undefined;
  return n;
}

/**
 * Alinea paso 1 y unidades guardadas aunque `productCode` difiera (API vs formulario).
 * Prioriza productId para evitar falsos "reducción" al subir cantidad (ej. 2→3).
 */
export function correlationKeyFromItem(item: ProductItem): string | null {
  const pid = normalizeProductIdForKey((item as { productId?: unknown }).productId);
  if (pid != null) return `id:${pid}`;
  const code = (item.productCode ?? '').trim();
  if (code) return `code:${code}`;
  return null;
}

export function correlationKeyFromUnit(u: {
  productId?: number | string;
  productCode?: string;
}): string | null {
  const rawId = (u as { productId?: unknown }).productId;
  const pid = typeof rawId === 'number' ? rawId : parseInt(String(rawId ?? ''), 10);
  if (!Number.isNaN(pid) && pid > 0) return `id:${pid}`;
  const code = (u.productCode ?? '').trim();
  if (code) return `code:${code}`;
  return null;
}

/**
 * Mantiene unidades cuyo producto sigue en el paso 1 (misma clave id/código o nombre).
 * Usar al quitar filas o al guardar para no armar el payload con productos ya retirados.
 */
export function filterProductUnitsToProductItems(
  units: ReadonlyArray<{
    productCode?: string;
    productId?: number | string;
    productName?: string;
  }>,
  items: ProductItem[],
): any[] {
  return units.filter((u) => {
    const ukey = correlationKeyFromUnit(u);
    for (const item of items) {
      if (!item.productName?.trim()) continue;
      const ikey = correlationKeyFromItem(item);
      if (ikey && ukey && ikey === ukey) return true;
    }
    const uName = (u.productName || '').trim().toLowerCase();
    if (!uName) return false;
    return items.some(
      (it) => it.productName?.trim() && it.productName.trim().toLowerCase() === uName,
    );
  });
}

/** Cantidad esperada de unidades por clave (id o código). */
export function getExpectedCountsByProductCode(items: ProductItem[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const item of items) {
    if (!item.productName?.trim()) continue;
    const key = correlationKeyFromItem(item);
    if (!key) continue;
    const qty = Math.max(1, parseInt(String(item.quantity), 10) || 1);
    m.set(key, (m.get(key) ?? 0) + qty);
  }
  return m;
}

/**
 * Deja a lo sumo N unidades por producto (N = suma de cantidades en paso 1), en el orden de `units`.
 * Alinea unidades con una cantidad bajada (ej. 4 → 3) sin vaciar el resto.
 */
export function trimProductUnitsToProductItems(
  productItems: ProductItem[],
  units: ReadonlyArray<{
    productCode?: string;
    productId?: number | string;
    productName?: string;
  }>,
): any[] {
  const expected = getExpectedCountsByProductCode(productItems);
  if (expected.size === 0) return [];
  const kept = new Map<string, number>();
  for (const k of expected.keys()) {
    kept.set(k, 0);
  }
  const out: any[] = [];
  for (const u of units) {
    let k = correlationKeyFromUnit(u);
    if (!k) {
      const uName = (u.productName || '').trim().toLowerCase();
      if (!uName) continue;
      for (const it of productItems) {
        if (!it.productName?.trim()) continue;
        if (it.productName.trim().toLowerCase() === uName) {
          k = correlationKeyFromItem(it);
          break;
        }
      }
    }
    if (!k || !expected.has(k)) continue;
    const max = expected.get(k)!;
    const c = kept.get(k) ?? 0;
    if (c < max) {
      out.push(u);
      kept.set(k, c + 1);
    }
  }
  return out;
}

export function getCountsByProductCodeFromUnits(
  units: ReadonlyArray<{ productCode?: string; productId?: number | string }>,
): Map<string, number> {
  const m = new Map<string, number>();
  for (const u of units) {
    const key = correlationKeyFromUnit(u);
    if (!key) continue;
    m.set(key, (m.get(key) ?? 0) + 1);
  }
  return m;
}

/**
 * Disminuyó filas, cantidad o se quitó un producto: las propiedades guardadas de las unidades
 * eliminadas ya no aplican.
 */
export function isReductionVersusSavedUnits(
  productItems: ProductItem[],
  savedUnits: ReadonlyArray<{ productCode?: string; productId?: number | string }>,
): boolean {
  if (!savedUnits.length) return false;
  const oldM = getCountsByProductCodeFromUnits(savedUnits);
  const newM = getExpectedCountsByProductCode(productItems);
  for (const [k, oldCount] of oldM) {
    const n = newM.get(k);
    if (n === undefined || n < oldCount) return true;
  }
  return false;
}

/**
 * Ningún producto bajó de cantidad respecto a lo guardado: se pueden añadir filas o subir cantidades
 * y conservar unidades existentes reutilizando en orden.
 */
export function isStrictlyAdditiveVersusSavedUnits(
  productItems: ProductItem[],
  savedUnits: ReadonlyArray<{ productCode?: string; productId?: number | string }>,
): boolean {
  if (!savedUnits.length) return true;
  const oldM = getCountsByProductCodeFromUnits(savedUnits);
  const newM = getExpectedCountsByProductCode(productItems);
  for (const [k, oldCount] of oldM) {
    const n = newM.get(k);
    if (n === undefined || n < oldCount) return false;
  }
  return true;
}

function catalogFieldToString(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string' && value.trim() === '') return '';
  return String(value);
}

function normalizeProductId(raw: unknown): number | undefined {
  return normalizeProductIdForKey(raw);
}

function findCatalogProduct(item: ProductItem, products: ProductData[]): ProductData | undefined {
  const nid = normalizeProductId((item as { productId?: unknown }).productId);
  if (nid != null) {
    const byId = products.find((p) => p.id === nid);
    if (byId) return byId;
  }
  const name = (item.productName ?? '').trim();
  const code = (item.productCode ?? '').trim();
  if (name && code) {
    const byNameAndCode = products.find((p) => p.name === name && p.code === code);
    if (byNameAndCode) return byNameAndCode;
  }
  if (name) {
    const byName = products.find((p) => p.name === name);
    if (byName) return byName;
  }
  if (code) {
    return products.find((p) => p.code === code);
  }
  return undefined;
}

function takeFromPool(
  pools: Map<string, MergedProductUnit[]>,
  item: ProductItem,
  codeForCodeKey: string,
): MergedProductUnit | undefined {
  const k1 = correlationKeyFromItem(item);
  if (k1) {
    const q = pools.get(k1);
    if (q && q.length > 0) {
      return q.shift();
    }
  }
  const c = codeForCodeKey.trim();
  if (c) {
    const codeKey = `code:${c}`;
    const q2 = pools.get(codeKey);
    if (q2 && q2.length > 0) {
      return q2.shift();
    }
  }
  return undefined;
}

/** Unidad compatible con el estado del paso 2 (ProductPropertiesStep). */
export type MergedProductUnit = {
  id: string;
  productId: number;
  productName: string;
  productCode: string;
  quantity: number;
  properties: {
    supplierId?: string;
    materialId: string;
    materialName: string;
    weight: string;
    length: string;
    diameter: string;
    color: string;
    workshopCode: string;
  };
  costs: { costPrice: string; salePrice: string };
};

/**
 * Construye la lista de unidades: reutiliza en orden (por filas del paso 1) unidades existentes
 * y crea con catálogo solo las faltantes.
 */
export function mergeUnitsAdditive(
  productItems: ProductItem[],
  products: ProductData[],
  existingUnits: ReadonlyArray<MergedProductUnit | Record<string, unknown>>,
  selectedSupplierId: string | undefined,
): MergedProductUnit[] {
  const validProductItems = productItems.filter(
    (item) => (item.productName ?? '').trim() !== '' && correlationKeyFromItem(item) != null,
  );

  const pools = new Map<string, MergedProductUnit[]>();
  for (const u of existingUnits) {
    const raw = u as MergedProductUnit;
    const k = correlationKeyFromUnit(raw);
    if (!k) continue;
    if (!pools.has(k)) pools.set(k, []);
    pools.get(k)!.push({ ...raw, properties: { ...raw.properties }, costs: { ...raw.costs } });
  }

  const merged: MergedProductUnit[] = [];
  let seq = 0;

  for (const item of validProductItems) {
    const product = findCatalogProduct(item, products);
    const displayCode = (item.productCode ?? product?.code ?? '').trim();
    const quantity = parseInt(String(item.quantity), 10) || 1;
    const resolvedProductId = product?.id ?? normalizeProductId(item.productId) ?? 0;

    for (let j = 0; j < quantity; j++) {
      const reused = takeFromPool(pools, item, displayCode);
      if (reused) {
        merged.push({
          ...reused,
          productName: item.productName,
          productCode: displayCode || reused.productCode,
          productId: resolvedProductId || reused.productId,
        });
        seq += 1;
        continue;
      }

      const id = `${displayCode || 'p'}-n-${seq++}-${j}-${Date.now()}`;
      merged.push({
        id,
        productId: resolvedProductId,
        productName: item.productName,
        productCode: displayCode,
        quantity: 1,
        properties: {
          supplierId: selectedSupplierId || '',
          materialId: product?.material?.id ? product.material.id.toString() : '',
          materialName: product?.material?.name || '',
          weight: catalogFieldToString(product?.weightGrams),
          length: catalogFieldToString(product?.lengthMm),
          diameter: catalogFieldToString(product?.diameterSize),
          color: catalogFieldToString(product?.color),
          workshopCode: catalogFieldToString(product?.workshopCode),
        },
        costs: {
          costPrice: catalogFieldToString(product?.costPrice),
          salePrice: catalogFieldToString(product?.salePrice),
        },
      });
    }
  }

  return merged;
}
